import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import '../../style/EditFoundPetPage.css';
import { useTranslation } from 'react-i18next';
import petSpecies from '../../data/petSpecies';
import foundPetOutcomes from '../../data/foundPetOutcomes';
import i18n from 'i18next';

const MAP_CONTAINER_STYLE = { width: '100%', height: '300px' };
const DEFAULT_CENTER = { lat: 41.3851, lng: 2.1734 };

const EditFoundPetPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [foundPet, setFoundPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [manageToken, setManageToken] = useState('');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // Form fields
    const [petType, setPetType] = useState('dog');
    const [description, setDescription] = useState('');
    const [foundDate, setFoundDate] = useState('');
    const [foundTime, setFoundTime] = useState('');
    const [foundAddress, setFoundAddress] = useState('');
    const [foundCoordinates, setFoundCoordinates] = useState(null);
    const [outcome, setOutcome] = useState('left_on_place');
    const [outcomeAddress, setOutcomeAddress] = useState('');
    const [outcomeCoordinates, setOutcomeCoordinates] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [reporterName, setReporterName] = useState('');
    const [reporterEmail, setReporterEmail] = useState('');

    // Google Maps
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places', 'maps'],
    });
    const foundAutocompleteRef = React.useRef(null);
    const outcomeAutocompleteRef = React.useRef(null);
    // Handlers for map and autocomplete
    const handleFoundPlaceChanged = () => {
        const ac = foundAutocompleteRef.current;
        if (!ac) return;
        const place = ac.getPlace();
        if (place.geometry && place.geometry.location) {
            setFoundCoordinates({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
            setFoundAddress(place.formatted_address);
        }
    };

    const handleOutcomePlaceChanged = () => {
        const ac = outcomeAutocompleteRef.current;
        if (!ac) return;
        const place = ac.getPlace();
        if (place.geometry && place.geometry.location) {
            setOutcomeCoordinates({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
            setOutcomeAddress(place.formatted_address);
        }
    };

    useEffect(() => {
        const fetchFoundPet = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/found-pets/${id}`);
                setFoundPet(response.data);
                // Set form fields
                setPetType(response.data.petType);
                setDescription(response.data.description);
                setPhoneNumber(response.data.reportedBy?.phoneNumber || response.data.reporterPhoneNumber || '');
                setReporterName(response.data.reporterName || '');
                setReporterEmail(response.data.reporterEmail || '');
                setOutcome(response.data.outcome);
                setFoundAddress(response.data.foundLocation?.address || '');
                setFoundCoordinates(response.data.foundLocation?.coordinates ? {
                    lat: response.data.foundLocation.coordinates[1],
                    lng: response.data.foundLocation.coordinates[0],
                } : null);
                setOutcomeAddress(response.data.outcomeLocation?.address || '');
                setOutcomeCoordinates(response.data.outcomeLocation?.coordinates ? {
                    lat: response.data.outcomeLocation.coordinates[1],
                    lng: response.data.outcomeLocation.coordinates[0],
                } : null);
                if (response.data.foundDateTime) {
                    const dt = new Date(response.data.foundDateTime);
                    setFoundDate(dt.toISOString().slice(0, 10));
                    setFoundTime(dt.toISOString().slice(11, 16));
                }
                // Auth check
                const storedUser = JSON.parse(localStorage.getItem('user'));
                if (storedUser && storedUser.token && response.data.reportedBy) {
                    const jwtPayload = JSON.parse(atob(storedUser.token.split('.')[1]));
                    if (jwtPayload.id === response.data.reportedBy._id || jwtPayload._id === response.data.reportedBy._id) {
                        setIsOwner(true);
                    }
                }
            } catch (err) {
                setError(err.message || 'Error fetching found pet details');
            } finally {
                setLoading(false);
            }
        };
        fetchFoundPet();
    }, [id, baseUrl]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const formData = {
                petType,
                description,
                foundLocation: {
                    type: 'Point',
                    coordinates: [foundCoordinates.lng, foundCoordinates.lat],
                    address: foundAddress,
                },
                foundDateTime: new Date(`${foundDate}T${foundTime}`).toISOString(),
                outcome,
            };
            if (outcome === 'found_and_left_elsewhere' && outcomeCoordinates) {
                formData.outcomeLocation = {
                    type: 'Point',
                    coordinates: [outcomeCoordinates.lng, outcomeCoordinates.lat],
                    address: outcomeAddress,
                };
            }
            if (isOwner) {
                formData.reportedBy = foundPet.reportedBy?._id;
                formData.reporterPhoneNumber = phoneNumber;
            } else {
                formData.reporterName = reporterName;
                formData.reporterEmail = reporterEmail;
                formData.reporterPhoneNumber = phoneNumber;
                formData.manageToken = manageToken;
            }
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const headers = isOwner && storedUser?.token ? { Authorization: `Bearer ${storedUser.token}` } : {};
            await axios.put(`${baseUrl}/api/found-pets/${id}`, formData, { headers });
            enqueueSnackbar(t('generalPet.successMessageEdit'), { variant: 'success' });
            navigate(`/found-pet/${id}`);
        } catch (err) {
            setError(t('generalPet.errorMessageEdit'));
        } finally {
            setLoading(false);
        }
    };

    // Delete handler
    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this found pet?')) return;
        setLoading(true);
        setError(null);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const headers = isOwner && storedUser?.token ? { Authorization: `Bearer ${storedUser.token}` } : {};
            const data = isOwner ? {} : { manageToken };
            await axios.delete(`${baseUrl}/api/found-pets/${id}`, { data, headers });
            enqueueSnackbar('Found pet deleted successfully!', { variant: 'success' });
            navigate('/found-pets');
        } catch (err) {
            setError(t('editPetPage.confirmDelete'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <p>{t('editFoundPetPage.loading')}</p>;
    if (error) return <p style={{ color: 'red' }}>{t('editFoundPetPage.error')}</p>;
    if (!foundPet) return null;

  
    // Generic label translation for pet species and outcomes (as in ReportFoundPet.js)
    const getTranslatedLabel = (options, value) => {
        const option = options.find(opt => opt.value === value);
        return option ? option.label[i18n.language] || option.label.en : value;
    };


    return (
        <div className="edit-found-pet-page">
            <h2>{t('editFoundPetPage.title')}</h2>
            <form onSubmit={handleSubmit}>
                <div>
                    <label>{t('editFoundPetPage.locationFound')}</label>
                    <Autocomplete onLoad={ac => (foundAutocompleteRef.current = ac)} onPlaceChanged={handleFoundPlaceChanged}>
                        <input
                            type="text"
                            value={foundAddress}
                            onChange={e => setFoundAddress(e.target.value)}
                            placeholder={t('editFoundPetPage.enterAddress')}
                            required
                        />
                    </Autocomplete>
                    {isLoaded && foundCoordinates && (
                        <div className="map-preview">
                            <GoogleMap
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                center={foundCoordinates}
                                zoom={15}
                            >
                                <Marker position={foundCoordinates} />
                            </GoogleMap>
                        </div>
                    )}
                </div>
                <div>
                    <label>{t('editFoundPetPage.petType')}</label>
                    <select value={petType} onChange={e => setPetType(e.target.value)} required>
                        {petSpecies.map(species => (
                            <option key={species.value} value={species.value}>
                                {getTranslatedLabel(petSpecies, species.value)}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label>{t('editFoundPetPage.dateFound')}</label>
                    <input type="date" value={foundDate} onChange={e => setFoundDate(e.target.value)} required />
                </div>
                <div>
                    <label>{t('editFoundPetPage.timeFound')}</label>
                    <input type="time" value={foundTime} onChange={e => setFoundTime(e.target.value)} required />
                </div>
                <div>
                    <label>{t('editFoundPetPage.description')}</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} required />
                </div>
                <div>
                    <label>{t('editFoundPetPage.outcome')}</label>
                    <select value={outcome} onChange={e => setOutcome(e.target.value)} required>
                        {foundPetOutcomes.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {getTranslatedLabel(foundPetOutcomes, opt.value)}
                            </option>
                        ))}
                    </select>
                </div>
                {outcome === 'found_and_left_elsewhere' && (
                    <div>
                        <label>{t('editFoundPetPage.locationLeft')}</label>
                        <Autocomplete onLoad={ac => (outcomeAutocompleteRef.current = ac)} onPlaceChanged={handleOutcomePlaceChanged}>
                            <input
                                type="text"
                                value={outcomeAddress}
                                onChange={e => setOutcomeAddress(e.target.value)}
                                placeholder={t('editFoundPetPage.enterAddress')}
                            />
                        </Autocomplete>
                        {isLoaded && outcomeCoordinates && (
                            <div className="map-preview">
                                <GoogleMap
                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                    center={outcomeCoordinates}
                                    zoom={15}
                                >
                                    <Marker position={outcomeCoordinates} />
                                </GoogleMap>
                            </div>
                        )}
                    </div>
                )}
                {isOwner ? (
                    <div>
                        <label>{t('editFoundPetPage.phoneNumber')}</label>
                        <PhoneInput
                            country={'us'}
                            value={phoneNumber}
                            onChange={value => setPhoneNumber(value)}
                            inputProps={{
                                name: 'phone',
                                required: true,
                                autoFocus: false,
                            }}
                            enableSearch
                            placeholder={t('editFoundPetPage.enterPhone')}
                            containerClass="w-full"
                            inputClass="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            buttonClass="border border-gray-300 rounded-l-md"
                            dropdownClass="react-phone-dropdown"
                        />
                    </div>
                ) : (
                    <>
                        <div>
                            <label>{t('editFoundPetPage.name')}</label>
                            <input type="text" value={reporterName} onChange={e => setReporterName(e.target.value)} required />
                        </div>
                        <div>
                            <label>{t('editFoundPetPage.email')}</label>
                            <input type="email" value={reporterEmail} onChange={e => setReporterEmail(e.target.value)} required />
                        </div>
                        <div>
                            <label>{t('editFoundPetPage.phoneNumber')}</label>
                            <PhoneInput
                                country={'us'}
                                value={phoneNumber}
                                onChange={value => setPhoneNumber(value)}
                                inputProps={{
                                    name: 'phone',
                                    required: true,
                                    autoFocus: false,
                                }}
                                enableSearch
                                placeholder={t('editFoundPetPage.enterPhone')}
                                containerClass="w-full"
                                inputClass="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                buttonClass="border border-gray-300 rounded-l-md"
                                dropdownClass="react-phone-dropdown"
                            />
                        </div>
                        <div>
                            <label>{t('editFoundPetPage.manageToken')}</label>
                            <input type="text" value={manageToken} onChange={e => setManageToken(e.target.value)} required placeholder={t('editFoundPetPage.enterManageToken')} />
                        </div>
                    </>
                )}
                <div className="button-row">
                    <button type="submit" disabled={loading}>{loading ? t('editFoundPetPage.updating') : t('editFoundPetPage.update')}</button>
                    <button type="button" onClick={handleDelete} disabled={loading} style={{ background: '#dc3545', color: 'white' }}>
                        {loading ? t('editFoundPetPage.deleting') : t('editFoundPetPage.delete')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditFoundPetPage;
